import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import { useAuthStore } from '../store';

const API_URL = process.env.REACT_APP_BACKEND_URL || (typeof window !== 'undefined' ? ((window.location.hostname === 'devapp.soviewfx.fr' || window.location.hostname === 'devadmin.soviewfx.fr') ? 'https://devhub.soviewfx.fr' : ((window.location.hostname === 'app.soviewfx.fr' || window.location.hostname === 'admin.soviewfx.fr') ? 'https://hub.soviewfx.fr' : '')) : '');

export default function PaymentSuccess() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { token, checkAuth } = useAuthStore();
  const [status, setStatus] = useState('checking');
  
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (!sessionId) {
      navigate('/home');
      return;
    }
    
    const pollStatus = async (attempts = 0) => {
      if (attempts >= 5) {
        setStatus('timeout');
        return;
      }
      
      try {
        const response = await fetch(`${API_URL}/api/payments/status/${sessionId}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          },
          credentials: 'include'
        });
        
        if (!response.ok) {
          throw new Error('Failed to check status');
        }
        
        const data = await response.json();
        
        if (data.payment_status === 'paid') {
          setStatus('success');
          // Refresh user data to get updated credits
          await checkAuth();
          toast.success('Paiement réussi !');
          setTimeout(() => navigate('/home'), 2000);
        } else if (data.status === 'expired') {
          setStatus('expired');
        } else {
          // Continue polling
          setTimeout(() => pollStatus(attempts + 1), 2000);
        }
      } catch (error) {
        setTimeout(() => pollStatus(attempts + 1), 2000);
      }
    };
    
    pollStatus();
  }, [searchParams, token, navigate, checkAuth]);
  
  return (
    <div className="h-full w-full flex items-center justify-center bg-background p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="glass-light rounded-2xl p-8 text-center max-w-sm"
      >
        {status === 'checking' && (
          <>
            <div className="spinner mx-auto mb-4" />
            <p className="text-white font-bold">Vérification du paiement...</p>
          </>
        )}
        
        {status === 'success' && (
          <>
            <CheckCircle size={64} className="text-green-500 mx-auto mb-4" />
            <p className="text-white font-bold text-xl">Paiement réussi !</p>
            <p className="text-muted-foreground mt-2">Vos crédits ont été ajoutés.</p>
          </>
        )}
        
        {status === 'expired' && (
          <>
            <p className="text-red-400 font-bold text-xl">Session expirée</p>
            <p className="text-muted-foreground mt-2">Veuillez réessayer.</p>
            <button
              onClick={() => navigate('/home')}
              className="mt-4 btn-primary"
            >
              Retour
            </button>
          </>
        )}
        
        {status === 'timeout' && (
          <>
            <p className="text-yellow-400 font-bold">Vérification en cours...</p>
            <p className="text-muted-foreground mt-2">
              Veuillez vérifier votre email pour la confirmation.
            </p>
            <button
              onClick={() => navigate('/home')}
              className="mt-4 btn-secondary"
            >
              Retour
            </button>
          </>
        )}
      </motion.div>
    </div>
  );
}
